import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import TuitionManage from "features/teacher/components/student/TuitionManage";
import PreviewNotice from "features/owner/components/home/PreviewNotice";
import useTeacherHome from "features/teacher/hooks/useTeacherHome";
import useHeader from "shared/hooks/useHeader";
import ICON_ACCOUNT from "assets/images/icon_account.png";
import PROFILE_STUDENT_SVG from "assets/images/profile_student.svg";
import ICON_CLASS from "assets/images/icon_class.png";
import ICON_COUNSEL from "assets/images/icon_counsel.png";
import ICON_STUDY from "assets/images/icon_study.png";
import ICON_STATS from "assets/images/icon_stats.png";
import ICON_TEXTBOOK from "assets/images/icon_textbook.png";
import NEXT_SVG from "assets/images/next.svg";
import { hw, scrollbar } from "features/homework/homeworkUi";

const menus = [
    { title: "계정관리", desc: "강사 계정 등록·수정", to: "/teacher/account", icon: ICON_ACCOUNT },
    { title: "학생관리", desc: "원생 정보·출결 관리", to: "/teacher/student", icon: PROFILE_STUDENT_SVG },
    { title: "학급관리", desc: "학급 생성·학생 배정", to: "/teacher/lesson/view/lesson", icon: ICON_CLASS },
    { title: "상담등록", desc: "상담 기록·확인", to: "/teacher/counsel", icon: ICON_COUNSEL },
    { title: "학습관리", desc: "과제 조회·배포", to: "/teacher/report", icon: ICON_STUDY },
    { title: "통계", desc: "원생·학급·미납 현황", to: "/teacher/stats", icon: ICON_STATS },
    { title: "교재구매", desc: "토스 교재 구매", href: "https://tossedu.imweb.me/tossproduct", icon: ICON_TEXTBOOK },
];

const TeacherHome = () => {
    const navigate = useNavigate();
    const { isBranchAccount } = useTeacherHome();
    const { name } = useHeader();

    const openMenu = (item: (typeof menus)[number]) => {
        if (item.href) {
            window.open(item.href, "_blank", "noopener,noreferrer");
            return;
        }
        if (item.to) navigate(item.to);
    };

    return (
        <Page>
            <PageHeader>
                <Title>{name ? `${name} 선생님, 안녕하세요` : "안녕하세요"}</Title>
                <Desc>계정·학생·학급·학습·통계를 한곳에서 바로 시작하세요.</Desc>
            </PageHeader>
            <Grid>
                {menus.map((item) => (
                    <Tile key={item.title} type="button" onClick={() => openMenu(item)}>
                        <IconWrap>
                            <img src={item.icon} alt="" />
                        </IconWrap>
                        <TileText>
                            <TileTitle>{item.title}</TileTitle>
                            <TileDesc>{item.desc}</TileDesc>
                        </TileText>
                        <Arrow src={NEXT_SVG} alt="" />
                    </Tile>
                ))}
            </Grid>
            <Panels $branch={isBranchAccount}>
                <NoticeCard>
                    <NoticeHead>
                        <div>
                            <NoticeTitle>공지사항</NoticeTitle>
                            <NoticeDesc>최근 공지를 확인하고 전체 목록으로 이동할 수 있습니다.</NoticeDesc>
                        </div>
                        <MoreButton type="button" onClick={() => navigate("/teacher/notice")}>
                            전체보기
                            <img src={NEXT_SVG} alt="" />
                        </MoreButton>
                    </NoticeHead>
                    <NoticeBody>
                        <PreviewNotice teacher={true} />
                    </NoticeBody>
                </NoticeCard>
                {isBranchAccount ? <TuitionManage /> : null}
            </Panels>
        </Page>
    );
};

const Page = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    padding: 20px 24px;
`;

const PageHeader = styled.div`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 10px 16px;
    flex-shrink: 0;
`;

const Title = styled.h1`
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: ${hw.text};
`;

const Desc = styled.p`
    margin: 0;
    font-size: 14px;
    color: ${hw.textMuted};
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    flex-shrink: 0;

    @media (max-width: 1400px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media (max-width: 800px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const Tile = styled.button`
    appearance: none;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 20px 18px;
    min-height: 88px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
    cursor: pointer;
    min-width: 0;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
        border-color: ${hw.brandMuted};
        box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
    }

    &:focus-visible {
        outline: none;
        border-color: ${hw.brand};
        box-shadow: 0 0 0 3px rgba(223, 0, 0, 0.12);
    }
`;

const IconWrap = styled.span`
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: transparent;

    img {
        width: 52px;
        height: 52px;
        object-fit: contain;
    }
`;

const TileText = styled.span`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    flex: 1;
`;

const Arrow = styled.img`
    width: 16px;
    height: 16px;
    opacity: 0.35;
    flex-shrink: 0;
    ${Tile}:hover & {
        opacity: 1;
    }
`;

const TileTitle = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: ${hw.text};
    letter-spacing: -0.02em;
`;

const TileDesc = styled.div`
    font-size: 13px;
    color: ${hw.textMuted};
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Panels = styled.div<{ $branch: boolean }>`
    display: grid;
    grid-template-columns: ${props => props.$branch ? "minmax(280px, 0.85fr) minmax(0, 1.35fr)" : "minmax(0, 1fr)"};
    gap: 16px;
    flex: 1;
    min-height: 0;

    @media (max-width: 1100px) {
        grid-template-columns: minmax(0, 1fr);
        overflow: auto;
        ${scrollbar}
    }
`;

const NoticeCard = styled.section`
    display: flex;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
    height: 100%;
    padding: 18px 20px 12px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
`;

const NoticeHead = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${hw.border};
    flex-shrink: 0;
`;

const NoticeTitle = styled.h2`
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: ${hw.text};
    letter-spacing: -0.02em;
`;

const NoticeDesc = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${hw.textMuted};
`;

const NoticeBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding-top: 12px;
    ${scrollbar}
`;

const MoreButton = styled.button`
    appearance: none;
    border: none;
    background: transparent;
    color: ${hw.brand};
    font-size: 13px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    padding: 4px 0;
    flex-shrink: 0;

    img {
        width: 14px;
        height: 14px;
    }

    &:hover {
        text-decoration: underline;
    }
`;

export default TeacherHome;
